import { useCallback, useMemo } from 'react'
import lucene, { AST } from 'lucene'
import { useLocation, useNavigate } from 'react-router-dom'
import { SearchPromiseClient } from '@trustero/trustero-api-web/lib/search/search_grpc_web_pb'
import {
  SearchOptions,
  SearchRequest,
  SearchResponse,
} from '@trustero/trustero-api-web/lib/search/search_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import queryString from 'query-string'
import {
  AdvancedSearchOptions,
  SearchableData,
  SearchResults,
} from './search.types'
import {
  buildAstFromInputs,
  parseSearchQuery,
  searchOptionsToQuery,
} from './search.helpers'
import { performMetadataSearch } from './search'

export const useSearch = (
  paramPrefix?: string,
): ((
  query: string,
  advancedSearch?: AdvancedSearchOptions,
  contentSearch?: boolean,
) => void) => {
  const location = useLocation()
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const navigateFallback = useNavigate()
  const {
    navigateState: {
      navigate,
      parsedQueryParams,
      locationSearchString: contextLocationSearchString,
    },
  } = useNavigateContext()
  const search = useCallback(
    (
      query: string,
      advancedSearch?: AdvancedSearchOptions,
      contentSearch?: boolean,
    ) => {
      const searchParams = parsedQueryParams || searchParamsFallback
      const locationSearchString =
        contextLocationSearchString || location.search
      const navigateFunc = navigate || navigateFallback
      let searchParamValue
      const paramName = paramPrefix ? `${paramPrefix}Search` : 'search'
      const contentParamName = paramPrefix
        ? `${paramPrefix}ContentSearch`
        : 'contentSearch'
      if (!advancedSearch) {
        if (!query.trim()) {
          navigateFunc(
            queryString.exclude(`${location.pathname}${locationSearchString}`, [
              paramName,
            ]),
          )
          return
        }
        const parsedQuery = parseSearchQuery(query.trim())
        searchParamValue = searchOptionsToQuery(parsedQuery)
      } else {
        const ast = buildAstFromInputs(advancedSearch)
        searchParamValue = lucene.toString(ast as AST)
      }
      const updatedParams: queryString.StringifiableRecord = {
        ...searchParams,
        [paramName]: encodeURIComponent(searchParamValue),
      }
      if (contentSearch) {
        updatedParams[contentParamName] = 'true'
      }
      if (searchParams['page']) {
        updatedParams['page'] = 1
      }
      const updatedHref = searchParamValue
        ? queryString.stringifyUrl(
            {
              url: location.pathname,
              query: updatedParams,
            },
            { arrayFormat: 'bracket' },
          )
        : queryString.exclude(`${location.pathname}${locationSearchString}`, [
            paramName,
            contentParamName,
          ])
      navigateFunc(updatedHref)
    },
    [
      navigate,
      navigateFallback,
      parsedQueryParams,
      searchParamsFallback,
      contextLocationSearchString,
      location,
      paramPrefix,
    ],
  )

  return search
}

export const useContentSearch = ({
  searchOptions,
  contentIds,
  shouldFetch = true,
}: {
  searchOptions: SearchOptions
  contentIds: string[]
  shouldFetch?: boolean
}): GrpcResponse<SearchResponse> => {
  const request = new SearchRequest()
    .setSearchOptions(searchOptions)
    .setContentIdsList(contentIds)
  const { response } = useSwrImmutableGrpc(
    SearchPromiseClient.prototype.search,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useSearchQuery = (paramPrefix?: string): string => {
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const paramName = paramPrefix ? `${paramPrefix}Search` : 'search'
  const searchParams = parsedQueryParams || searchParamsFallback
  const query = searchParams[paramName] as string
  const decodedQuery = query ? decodeURIComponent(query) : ''
  return decodedQuery
}

export const useIsContentSearchActive = (paramPrefix?: string): boolean => {
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const contentParamName = paramPrefix
    ? `${paramPrefix}ContentSearch`
    : 'contentSearch'
  const searchParams = parsedQueryParams || searchParamsFallback
  const contentSearch = (searchParams[contentParamName] as string) || 'false'
  return contentSearch === 'true'
}

export const useApplySearch = <T>(
  items: SearchableData<T>[],
  paramPrefix?: string,
): SearchResults<T> => {
  let filteredData = items
  const query = useSearchQuery(paramPrefix)
  const isContentSearchActive = useIsContentSearchActive(paramPrefix)
  // parse the query
  const searchOptions = parseSearchQuery(query)
  const contentIds: string[] = items
    .map((item) => item.contentIds)
    .filter((ids) => ids !== undefined)
    .flat() as string[]
  const contentMatches = useMemo(() => {
    return new Set<string>()
  }, [])
  const { data: contentSearchResponse } = useContentSearch({
    searchOptions,
    contentIds,
    shouldFetch: isContentSearchActive && query.trim() !== '',
  })
  const metadataSearchResponse = performMetadataSearch(
    filteredData,
    searchOptions,
  )

  // filter data by excluded content ids
  filteredData = filteredData.filter((item) => {
    // first check if the item should be excluded by metadata search
    if (metadataSearchResponse.excludedIds.includes(item.id)) {
      return false
    }

    if (!item.contentIds || item.contentIds.length === 0) {
      // if it wasn't excluded by metadata search, and there are no content ids, include it
      return true
    }
    // otherwise, return true if all content ids are not excluded by content search
    return item.contentIds.every(
      (id) => !contentSearchResponse?.getExcludedContentIdsList().includes(id),
    )
  })
  // get the unique matches
  const uniqueMatches: SearchableData<T>[] = []
  filteredData.forEach((item) => {
    if (!item.contentIds || item.contentIds.length === 0) {
      // if there are no content ids, check if the item is a metadata match
      if (metadataSearchResponse.includedIds.includes(item.id)) {
        uniqueMatches.push(item)
      }
      return
    }
    if (
      metadataSearchResponse.includedIds.includes(item.id) ||
      item.contentIds.some((id) =>
        contentSearchResponse?.getIncludedContentIdsList().includes(id),
      )
    ) {
      if (
        item.contentIds.some((id) => {
          return contentSearchResponse?.getIncludedContentIdsList().includes(id)
        })
      ) {
        contentMatches.add(item.id)
      }
      uniqueMatches.push(item)
    }
  })
  const data = uniqueMatches.map((item) => item.original)
  return {
    data,
    contentMatches,
  } as SearchResults<T>
}
